/**
 * PalladiumMap — Leaflet + OpenStreetMap Initializer
 *
 * Renders every <div class="pd-leaflet-map" data-pb-*> on the public frontend
 * and wires click-to-locate: clicking any element matching the configured city
 * selector geocodes its text via Nominatim (OSM) and flies the map to it.
 *
 * No API key. Geocode results cached in localStorage. Uses a MutationObserver
 * to catch Blazor Server interactive renders (same pattern as swiper-init.js).
 *
 * Config (data-pb-* on the map div):
 *   city-selector  CSS selector for clickable city chips   (default ".pd-map-city")
 *   geo-suffix     appended to chip text before geocoding   (e.g. ", Girona, Spain")
 *   click-zoom     zoom level applied on a city click       (default 13)
 *   map-height     CSS height of the map                    (default "420px")
 *   center-lat/lng/init-zoom   initial view
 */
(function () {
    'use strict';

    // Self-hosted (vendored leaflet@1.9.4) — marker/control images auto-resolve from
    // the images/ dir beside leaflet.css. No cross-origin request to block.
    var LEAFLET_JS = '/_content/Palladium.PageBuilder/js/lib/leaflet/leaflet.js';
    var LEAFLET_CSS = '/_content/Palladium.PageBuilder/js/lib/leaflet/leaflet.css';
    var NOMINATIM = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=';
    var CACHE_PREFIX = 'pd-geo:';

    var maps = {};            // mapId -> { map, marker, el }
    var _observer = null;
    var _pendingInit = null;
    var _geocoding = false;

    // ── Asset loading ─────────────────────────────────────────────────────────

    // Shared loader (pd-load.js, loaded before this file).
    function loadCSS(url) { PdLoad.css(url); }

    function loadScript(url) { return PdLoad.script(url, function () { return !!window.L; }); }

    // ── Attribute helpers ───────────────────────────────────────────────────────

    function attr(el, name, fallback) {
        var v = el.getAttribute('data-pb-' + name);
        return v !== null && v !== '' ? v : fallback;
    }
    function attrFloat(el, name, fallback) {
        var n = parseFloat(attr(el, name, ''));
        return isNaN(n) ? fallback : n;
    }
    function attrInt(el, name, fallback) {
        var n = parseInt(attr(el, name, ''), 10);
        return isNaN(n) ? fallback : n;
    }

    // ── Geocoding (Nominatim + localStorage cache) ──────────────────────────────

    function geocode(query) {
        var key = CACHE_PREFIX + query.toLowerCase();
        try {
            var cached = localStorage.getItem(key);
            if (cached) return Promise.resolve(JSON.parse(cached));
        } catch (e) { /* ignore */ }

        return fetch(NOMINATIM + encodeURIComponent(query), {
            headers: { 'Accept': 'application/json' },
        })
            .then(function (r) { return r.json(); })
            .then(function (data) {
                if (!data || !data.length) return null;
                var hit = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
                try { localStorage.setItem(key, JSON.stringify(hit)); } catch (e) { /* ignore */ }
                return hit;
            })
            .catch(function () { return null; });
    }

    // ── Init ────────────────────────────────────────────────────────────────────

    async function initAll() {
        var els = document.querySelectorAll('.pd-leaflet-map:not([data-map-ready])');
        if (els.length === 0) return;

        loadCSS(LEAFLET_CSS);
        await loadScript(LEAFLET_JS);

        els.forEach(function (el) {
            if (el._mapInitialized) return;
            initOne(el);
        });
    }

    function initOne(el) {
        var id = el.id;
        if (!id) {
            id = 'pdmap-' + Math.random().toString(36).substr(2, 8);
            el.id = id;
        }
        if (maps[id]) { try { maps[id].map.remove(); } catch (e) {} delete maps[id]; }

        var height = attr(el, 'map-height', '420px');
        el.style.height = height;

        // Fixed-marker mode (GeoPointPart via DynField tag="map"): a pin with an
        // optional place-label popup, centered on the point unless overridden.
        var mLat = attrFloat(el, 'marker-lat', NaN);
        var mLng = attrFloat(el, 'marker-lng', NaN);
        var hasMarker = !isNaN(mLat) && !isNaN(mLng);

        var lat = attrFloat(el, 'center-lat', hasMarker ? mLat : 42.2619);
        var lng = attrFloat(el, 'center-lng', hasMarker ? mLng : 3.1764);
        var initZoom = attrInt(el, 'init-zoom', hasMarker ? 15 : 9);

        var map = window.L.map(el, { scrollWheelZoom: true }).setView([lat, lng], initZoom);
        window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            // OSM serves an "Access blocked" (403) tile when the request carries no Referer.
            // The page policy is no-referrer (OrchardCore.Security), so force it per <img>.
            referrerPolicy: 'strict-origin-when-cross-origin',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
            maxZoom: 19,
        }).addTo(map);

        maps[id] = { map: map, marker: null, el: el };

        if (hasMarker) {
            var label = attr(el, 'marker-label', '');
            var pin = window.L.marker([mLat, mLng]).addTo(map);
            if (label) pin.bindPopup(label);
            maps[id].marker = pin;
        }
        el._mapInitialized = true;
        el.setAttribute('data-map-ready', '');

        // Leaflet often mounts before the container has its final size (Blazor).
        setTimeout(function () { try { map.invalidateSize(); } catch (e) {} }, 200);
    }

    // ── Click-to-locate (event delegation across all maps) ──────────────────────

    function onCityClick(e) {
        var ids = Object.keys(maps);
        for (var i = 0; i < ids.length; i++) {
            var entry = maps[ids[i]];
            var selector = attr(entry.el, 'city-selector', '.pd-map-city');
            var chip;
            try { chip = e.target.closest(selector); } catch (ex) { continue; }
            if (!chip) continue;

            var name = (chip.getAttribute('data-pb-place') || chip.textContent || '').trim();
            if (!name) continue;

            e.preventDefault();
            locate(entry, name);
            return;
        }
    }

    function parseOverrides(el) {
        var raw = el.getAttribute('data-pb-city-overrides');
        if (!raw) return {};
        try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
    }

    function placeMarker(entry, lat, lng, name, zoom) {
        var L = window.L;
        if (entry.marker) entry.map.removeLayer(entry.marker);
        entry.marker = L.circleMarker([lat, lng], {
            radius: 9, color: '#fff', weight: 2,
            fillColor: '#ef8b00', fillOpacity: 1,
        }).addTo(entry.map).bindPopup(name).openPopup();
        entry.map.flyTo([lat, lng], zoom, { duration: 0.8 });
        try { entry.el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    }

    function locate(entry, name) {
        // Street-level zoom. Precise locations (pin/address) go tighter than a
        // plain town-name geocode. The "Zoom au clic" trait acts as a floor.
        var base = attrInt(entry.el, 'click-zoom', 16);

        // 1. Per-city override: exact pin wins, then a custom address.
        var ov = parseOverrides(entry.el)[name];
        if (ov && ov.lat != null && ov.lng != null) {
            placeMarker(entry, ov.lat, ov.lng, name, Math.max(base, 17));
            return;
        }

        if (_geocoding) return;
        var precise = !!(ov && ov.addr);
        var query = precise ? ov.addr : name + (attr(entry.el, 'geo-suffix', '') || '');

        _geocoding = true;
        geocode(query).then(function (hit) {
            _geocoding = false;
            if (!hit) return;
            placeMarker(entry, hit.lat, hit.lng, name, Math.max(base, precise ? 17 : 15));
        });
    }

    // ── Blazor re-render observer ────────────────────────────────────────────────

    function startObserver() {
        if (_observer) return;
        _observer = new MutationObserver(function () {
            if (_pendingInit) clearTimeout(_pendingInit);
            _pendingInit = setTimeout(function () {
                _pendingInit = null;
                if (document.querySelector('.pd-leaflet-map:not([data-map-ready])')) initAll();
            }, 150);
        });
        _observer.observe(document.body, { childList: true, subtree: true });
    }

    window.PalladiumMap = { initAll: initAll, initOne: initOne, geocode: geocode };

    document.addEventListener('click', onCityClick);

    if (document.body) {
        startObserver();
        initAll();
    } else {
        document.addEventListener('DOMContentLoaded', function () {
            startObserver();
            initAll();
        });
    }
})();
